import React from 'react';
import { X, CheckCircle2, FileText, AlertCircle } from 'lucide-react';

interface SiteNotesPreCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  onYes: () => void;
  onNo: () => void;
}

export default function SiteNotesPreCheckModal({ isOpen, onClose, onYes, onNo }: SiteNotesPreCheckModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl max-w-lg w-full border border-white/20">
        {/* Header */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Surveyor Site Notes</h2>
                  <p className="text-blue-100 text-sm">Before exporting your floor plan</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 backdrop-blur-3xl"></div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 bg-gradient-to-b from-gray-50/50 to-white">
          {/* Question */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-amber-900 mb-1">
                  Have you completed the Surveyor Site Notes?
                </h3>
                <p className="text-xs text-amber-700">
                  This information is essential for a complete and professional export.
                </p>
              </div>
            </div>
          </div>

          {/* What to Include */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              What you can add in Surveyor Site Notes:
            </h4>
            <ul className="space-y-2 text-xs text-blue-800">
              <li className="flex items-start">
                <span className="inline-block w-1.5 h-1.5 bg-blue-400 rounded-full mr-2 mt-1.5 flex-shrink-0"></span>
                <span><strong>Property Information:</strong> Address, survey date, assessor details</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-1.5 h-1.5 bg-blue-400 rounded-full mr-2 mt-1.5 flex-shrink-0"></span>
                <span><strong>Heating Systems:</strong> PCDB boiler data, system specifications, controls</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-1.5 h-1.5 bg-blue-400 rounded-full mr-2 mt-1.5 flex-shrink-0"></span>
                <span><strong>Building Fabric:</strong> Wall types, insulation levels, construction details</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-1.5 h-1.5 bg-blue-400 rounded-full mr-2 mt-1.5 flex-shrink-0"></span>
                <span><strong>Evidence Photos:</strong> Exterior, heating system, windows, ventilation</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-1.5 h-1.5 bg-blue-400 rounded-full mr-2 mt-1.5 flex-shrink-0"></span>
                <span><strong>Additional Notes:</strong> Special conditions, observations, recommendations</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            <button
              onClick={onYes}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center justify-center space-x-2"
            >
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">Yes, Continue</span>
            </button>
            <button
              onClick={onNo}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center justify-center space-x-2"
            >
              <FileText className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">No, Open Notes</span>
            </button>
          </div>

          {/* Skip Link */}
          <div className="text-center">
            <button
              onClick={onClose}
              className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancel export
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
